import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from '../../store';
import { PrimeArtifact } from '../../types/primeArtifact';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { createRootArtifact } from '../../store/slices/ArtifactSlice';
import { useArtifactTree } from '../../contexts';
import styles from './NoRootPrompt.module.css';

export const NoRootPrompt: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { selectedTree, reloadTree } = useArtifactTree();
  const [rootPrimeArtifacts, setRootPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRootPrimeArtifacts = async () => {
      setLoading(true);
      try {
        const roots = await PrimeArtifactService.getRootArtifacts();
        setRootPrimeArtifacts(roots);
      } catch (err) {
        setError('Failed to fetch root Prime artifacts');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    void fetchRootPrimeArtifacts();
  }, []);

  const handleCreateRoot = async (prime_artifact_id: string) => {
    if (!selectedTree) {
      console.error('No tree selected');
      return;
    }
    try {
      setLoading(true);
      await dispatch(createRootArtifact({ prime_artifact_id, treeId: selectedTree.id })).unwrap();
      await reloadTree();
      navigate('/artifacts');
    } catch (err) {
      setError('Failed to create root artifact');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading root Prime artifacts...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.noRootPrompt}>
      <h3>Select a Root Prime Artifact</h3>
      <p>Choose a root Prime artifact to initialize the Krysalis hierarchy:</p>
      <div className={styles.rootOptions}>
        {rootPrimeArtifacts.map((artifact) => (
          <div key={artifact.id} className={styles.rootOption}>
            <h4>{artifact.name}</h4>
            <p>{artifact.description}</p>
            <button
              onClick={() => handleCreateRoot(artifact.id)}
              className={styles.createRootButton}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Use as Root'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
